// store/modules/buses.js
import api from "@/api";

export default {
  namespaced: true,
  state: () => ({
    buses: [],
    currentBus: null,
    busAreas: [],
    busSubscriptions: [],
    isLoading: false,
    error: null,
    filters: {
      search: '',
      type: null,
      is_active: null
    },
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      perPage: 10,
      from: 0,
      to: 0
    }
  }),

  mutations: {
    SET_BUSES(state, buses) {
      state.buses = buses;
    },
    SET_CURRENT_BUS(state, bus) {
      state.currentBus = bus;
    },
    SET_BUS_AREAS(state, areas) {
      state.busAreas = areas;
    },
    SET_BUS_SUBSCRIPTIONS(state, subscriptions) {
      state.busSubscriptions = subscriptions;
    },
    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_FILTERS(state, filters) {
      state.filters = { ...state.filters, ...filters };
    },
    SET_PAGINATION(state, pagination) {
      state.pagination = { ...state.pagination, ...pagination };
    },
    ADD_BUS(state, bus) {
      state.buses.unshift(bus);
    },
    UPDATE_BUS(state, updatedBus) {
      const index = state.buses.findIndex(b => b.id === updatedBus.id);
      if (index !== -1) {
        state.buses.splice(index, 1, updatedBus);
      }
    },
    DELETE_BUS(state, busId) {
      state.buses = state.buses.filter(b => b.id !== busId);
    },
    ADD_BUS_AREA(state, area) {
      state.busAreas.push(area);
    },
    UPDATE_BUS_AREA(state, updatedArea) {
      const index = state.busAreas.findIndex(a => a.id === updatedArea.id);
      if (index !== -1) {
        state.busAreas.splice(index, 1, updatedArea);
      }
    },
    DELETE_BUS_AREA(state, areaId) {
      state.busAreas = state.busAreas.filter(a => a.id !== areaId);
    }
  },

  actions: {
    async fetchBuses({ commit, state }, { page = 1, perPage = 10, filters = {} } = {}) {
      try {
        commit('SET_LOADING', true);
        const params = {
          page,
          per_page: perPage,
          ...state.filters,
          ...filters
        };

        const response = await api.get('/buses', { params });
        
        // Handle different response structures
        let busesData = [];
        let paginationData = {};
        
        if (response.data && response.data.data) {
          // Laravel paginated response
          busesData = response.data.data;
          paginationData = {
            currentPage: response.data.meta?.current_page || 1,
            totalPages: response.data.meta?.last_page || 1,
            totalItems: response.data.meta?.total || 0,
            perPage: response.data.meta?.per_page || perPage,
            from: response.data.meta?.from || 0,
            to: response.data.meta?.to || 0
          };
        } else if (Array.isArray(response.data)) {
          // Simple array response
          busesData = response.data;
          paginationData = {
            currentPage: 1,
            totalPages: 1,
            totalItems: response.data.length,
            perPage: response.data.length,
            from: 1,
            to: response.data.length
          };
        } else {
          // Fallback
          busesData = [];
          paginationData = {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            perPage: perPage,
            from: 0,
            to: 0
          };
        }
        
        commit('SET_BUSES', busesData);
        commit('SET_PAGINATION', paginationData);
        
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchBus({ commit }, busId) {
      try {
        commit('SET_LOADING', true);
        const response = await api.get(`/buses/${busId}`);
        
        // Handle different response structures
        const busData = response.data.data || response.data;
        commit('SET_CURRENT_BUS', busData);
        return busData;
        
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async createBus({ commit }, busData) {
      try {
        commit('SET_LOADING', true);
        const response = await api.post('/buses', busData);
        
        const newBus = response.data.data || response.data;
        commit('ADD_BUS', newBus);
        return newBus;
        
      } catch (error) {
        if (error.response?.status === 422) {
          throw error;
        }
        commit('SET_ERROR', error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async updateBus({ commit }, { id, busData }) {
      try {
        commit('SET_LOADING', true);
        const response = await api.put(`/buses/${id}`, busData);
        
        const updatedBus = response.data.data || response.data;
        commit('UPDATE_BUS', updatedBus);
        return updatedBus;
        
      } catch (error) {
        if (error.response?.status === 422) {
          throw error;
        }
        commit('SET_ERROR', error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async deleteBus({ commit }, busId) {
      try {
        commit('SET_LOADING', true);
        await api.delete(`/buses/${busId}`);
        commit('DELETE_BUS', busId);
        
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // Bus Areas
    async fetchBusAreas({ commit }, busId) {
      try {
        commit('SET_LOADING', true);
        const response = await api.get(`/buses/${busId}/areas`);
        
        // Handle different response structures
        const areasData = response.data.data || response.data;
        commit('SET_BUS_AREAS', Array.isArray(areasData) ? areasData : []);
        return Array.isArray(areasData) ? areasData : [];
        
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async createBusArea({ commit }, { busId, areaData }) {
      try {
        commit('SET_LOADING', true);
        const response = await api.post(`/buses/${busId}/areas`, areaData);
        
        const newArea = response.data.data || response.data;
        commit('ADD_BUS_AREA', newArea);
        return newArea;
        
      } catch (error) {
        if (error.response?.status === 422) {
          throw error;
        }
        commit('SET_ERROR', error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async updateBusArea({ commit }, { busId, areaId, areaData }) {
      try {
        commit('SET_LOADING', true);
        const response = await api.put(`/buses/${busId}/areas/${areaId}`, areaData);
        
        const updatedArea = response.data.data || response.data;
        commit('UPDATE_BUS_AREA', updatedArea);
        return updatedArea;
        
      } catch (error) {
        if (error.response?.status === 422) {
          throw error;
        }
        commit('SET_ERROR', error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async deleteBusArea({ commit }, { busId, areaId }) {
      try {
        commit('SET_LOADING', true);
        await api.delete(`/buses/${busId}/areas/${areaId}`);
        commit('DELETE_BUS_AREA', areaId);
        
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // Bus Subscriptions
    async fetchBusSubscriptions({ commit }, params = {}) {
      try {
        commit('SET_LOADING', true);
        const response = await api.get('/bus-subscriptions', { params });
        
        // Handle different response structures
        const subscriptionsData = response.data.data || response.data;
        commit('SET_BUS_SUBSCRIPTIONS', Array.isArray(subscriptionsData) ? subscriptionsData : []);
        return Array.isArray(subscriptionsData) ? subscriptionsData : [];
        
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchBusSubscribers({ commit }, busId) {
      try {
        commit('SET_LOADING', true);
        const response = await api.get(`/buses/${busId}/subscribers`);
        
        // Handle different response structures
        const subscribersData = response.data.data || response.data;
        return Array.isArray(subscribersData) ? subscribersData : [];
        
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async cancelSubscription({ commit }, subscriptionId) {
      try {
        commit('SET_LOADING', true);
        const response = await api.post(`/bus-subscriptions/${subscriptionId}/cancel`);
        return response.data;
        
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    }
  },

  getters: {
    buses: state => state.buses,
    currentBus: state => state.currentBus,
    busAreas: state => state.busAreas,
    busSubscriptions: state => state.busSubscriptions,
    isLoading: state => state.isLoading,
    error: state => state.error,
    filters: state => state.filters,
    pagination: state => state.pagination
  }
};